import React from 'react';
import { Link } from 'react-router-dom';
import Table from '../Table';

// Same prefix recordStudioAudit writes. Stripping it here is what lets a
// Hebrew answer align RTL — the English bracket would otherwise pin the line LTR.
const CLARIFYING_PREFIX = '[answered clarifying question]';

const RESULT_CLASS = {
  stopped: 'bg-red-100 text-red-800',
  waiting: 'bg-blue-100 text-blue-800',
  shipped: 'bg-green-100 text-green-800',
  config: 'bg-emerald-100 text-emerald-800',
  answered: 'bg-sky-100 text-sky-800',
  no_change: 'bg-gray-100 text-gray-700',
};

const TURN_MODE_LABELS = {
  tweak: 'Tweak',
  new_feature: 'New feature',
  redesign: 'Redesign',
};

function formatDuration(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatCount(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n).toLocaleString();
}

function displayPrompt(prompt) {
  const text = String(prompt || '').replace(/\s+/g, ' ').trim();
  if (!text) return '—';
  if (text.startsWith(CLARIFYING_PREFIX)) {
    return text.slice(CLARIFYING_PREFIX.length).trim() || 'Clarifying answer';
  }
  return text;
}

function resultLabel(row) {
  switch (row.studio_result) {
    case 'shipped':
      return row.new_version ? `Shipped v${row.new_version}` : 'Shipped';
    case 'waiting':
      return 'Waiting';
    case 'answered':
      return 'Answered';
    case 'config':
      return 'Config saved';
    case 'stopped':
      return 'Stopped';
    default:
      return 'No change';
  }
}

function noteLine(row) {
  const parts = [];
  if (row.round_budget_exhausted) parts.push('round budget');
  if (row.aborted) parts.push('stopped by the user');
  return parts.join(' · ');
}

function ResultChip({ row }) {
  const key = row.studio_result || 'no_change';
  return (
    <span className={`inline-flex whitespace-nowrap px-2 py-1 text-xs font-semibold rounded-full ${RESULT_CLASS[key] || RESULT_CLASS.no_change}`}>
      {resultLabel(row)}
    </span>
  );
}

function AccountCell({ row }) {
  if (row.website_name) {
    return (
      <div className="text-sm">
        <Link
          to={`/websites/${row.website_id}`}
          onClick={(event) => event.stopPropagation()}
          dir="auto"
          className="font-medium text-primary-700 hover:underline"
        >
          {row.website_name}
        </Link>
        <div className="text-xs text-gray-500 truncate" title={row.user_email || row.user_name || ''}>{row.user_email || row.user_name || 'Unknown user'}</div>
        </div>
    );
  }
  if (row.account_id) {
    return (
      <div className="text-sm">
        <Link
          to={`/accounts/${row.account_id}`}
          onClick={(event) => event.stopPropagation()}
          dir="auto"
          className="font-medium text-primary-700 hover:underline"
          title="Site-less account — no website row"
        >
          {row.account_name || 'No site'}
        </Link>
        <div className="text-xs text-gray-500 truncate" title={row.user_email || row.user_name || ''}>{row.user_email || row.user_name || 'Unknown user'}</div>
        </div>
    );
  }
  return <span className="text-sm text-gray-500">Unknown website</span>;
}

function TokensCell({ row }) {
  const input = formatCount(row.input_tokens);
  const output = formatCount(row.output_tokens);
  if (input == null && output == null) {
    return <span className="text-sm text-gray-400" title="Token usage was not recorded for this turn">—</span>;
  }
  return (
    <div className="font-mono text-xs leading-4 text-gray-700">
      <div className="whitespace-nowrap" title="Input tokens">{input ?? '—'} in</div>
      <div className="whitespace-nowrap" title="Output tokens">{output ?? '—'} out</div>
    </div>
  );
}

function CreditsCell({ row }) {
  const credits = formatCount(row.credits_spent);
  if (credits == null) {
    return <span className="text-sm text-gray-400" title="No credit quote was recorded for this turn">—</span>;
  }
  return <span className="font-mono text-sm" title="Credits quoted for this turn">{credits}</span>;
}

function InsightCell({ row }) {
  const has = Boolean(row.has_agent_insight) && row.insight_development !== 'none';
  if (!has) {
    return <span className="text-sm text-gray-500">No</span>;
  }
  let developed = 'not developed';
  let developedClass = 'text-gray-600';
  if (row.insight_developed || row.insight_development === 'yes') {
    developed = 'developed';
    developedClass = 'text-green-700';
  } else if (row.insight_development === 'partial') {
    developed = 'partial';
    developedClass = 'text-amber-700';
  }
  return (
    <span className="block text-xs leading-4" title={`Agent insight: yes, ${developed}`}>
      <span className="font-semibold text-amber-800">Yes</span>
      <span className={`block whitespace-nowrap ${developedClass}`}>· {developed}</span>
    </span>
  );
}

function MetricCard({ label, value, helper, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`bg-white rounded-lg shadow p-4 text-left transition-shadow hover:shadow-md ${
        active ? 'ring-2 ring-primary-500' : ''
      }`}
    >
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900">{value ?? '—'}</p>
      {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
    </button>
  );
}

const STUDIO_COLUMNS = [
  {
    key: 'studio_result',
    label: 'Result',
    width: '10%',
    sortable: false,
    render: (row) => <ResultChip row={row} />,
  },
  {
    key: 'prompt',
    label: 'Request',
    width: '13%',
    sortable: false,
    render: (row) => {
      const prompt = displayPrompt(row.prompt);
      const note = noteLine(row);
      return (
        <div className="overflow-hidden">
          <p dir="auto" className="text-sm text-gray-900 line-clamp-2 break-words" title={prompt}>
            {prompt}
          </p>
          {note && <p className="mt-1 text-xs text-gray-500">{note}</p>}
          {row.error_message && (
            <p className="mt-1 text-xs text-red-600 line-clamp-2" title={row.error_message}>
              {row.error_message}
            </p>
          )}
        </div>
      );
    },
  },
  {
    key: 'turn_mode',
    label: 'Mode',
    width: '9%',
    sortable: false,
    render: (row) => (
      <span className="whitespace-nowrap text-sm text-gray-700">{TURN_MODE_LABELS[row.turn_mode] || '—'}</span>
    ),
  },
  {
    key: 'model_rounds_attempted',
    label: 'Rounds',
    width: '7%',
    render: (row) => <span className="font-mono text-sm">{row.model_rounds_attempted ?? '—'}</span>,
  },
  {
    key: 'duration_ms',
    label: 'Duration',
    width: '8%',
    render: (row) => <span className="text-sm text-gray-600">{formatDuration(row.duration_ms)}</span>,
  },
  {
    key: 'actions_failed',
    label: 'Retries',
    width: '7%',
    render: (row) => {
      const failures = Number(row.actions_failed) || 0;
      return (
        <span className={`font-mono text-sm ${failures > 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
          {failures}
        </span>
      );
    },
  },
  {
    key: 'token_total',
    label: 'Tokens',
    width: '8%',
    render: (row) => <TokensCell row={row} />,
  },
  {
    key: 'credits_spent',
    label: 'Credits spent',
    width: '11%',
    render: (row) => <CreditsCell row={row} />,
  },
  {
    key: 'agent_insight',
    label: 'Agent insight',
    width: '10%',
    sortable: false,
    render: (row) => <InsightCell row={row} />,
  },
  {
    key: 'account',
    label: 'Account',
    width: '9%',
    sortable: false,
    render: (row) => <AccountCell row={row} />,
  },
  {
    key: 'created_at',
    label: 'When',
    width: '8%',
    render: (row) => {
      if (!row.created_at) return '—';
      const created = new Date(row.created_at);
      return (
        <div className="text-xs text-gray-500">
          <div className="whitespace-nowrap">{created.toLocaleDateString()}</div>
          <div className="whitespace-nowrap">{created.toLocaleTimeString()}</div>
        </div>
      );
    },
  },
];

const StudioAuditBoard = ({
  audits,
  stats,
  loading,
  activeResult,
  onSelectResult,
  onOpen,
  sortBy,
  sortOrder,
  onSort,
  footer,
}) => {
  const studio = stats?.studio;
  const select = (result) => {
    if (!onSelectResult) return;
    onSelectResult(activeResult === result ? '' : result);
  };

  return (
    <div>
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
          <MetricCard
            label="Turns"
            value={stats.total}
            helper={`${stats.last24h || 0} in last 24h`}
            active={!activeResult}
            onClick={() => onSelectResult && onSelectResult('')}
          />
          <MetricCard
            label="Shipped a version"
            value={studio?.shipped ?? '—'}
            helper={studio ? `${studio.shippedRate}% of turns` : null}
            active={activeResult === 'shipped'}
            onClick={() => select('shipped')}
          />
          <MetricCard
            label="Waiting on the merchant"
            value={studio?.waiting ?? '—'}
            helper="Parked on a choice"
            active={activeResult === 'waiting'}
            onClick={() => select('waiting')}
          />
          <MetricCard
            label="Answered, nothing built"
            value={studio?.answered ?? '—'}
            helper="Clarifying answer, nothing written"
            active={activeResult === 'answered'}
            onClick={() => select('answered')}
          />
          <MetricCard
            label="Stopped"
            value={studio?.stopped ?? '—'}
            helper={`${stats.avgRounds ?? '—'} rounds · ${formatDuration(stats.avgDurationMs)} avg`}
            active={activeResult === 'stopped'}
            onClick={() => select('stopped')}
          />
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table
          columns={STUDIO_COLUMNS}
          data={audits}
          loading={loading}
          emptyMessage="No BOS Design Studio turns match these filters"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          onRowClick={onOpen}
          fixedLayout
        />
        {footer}
      </div>
    </div>
  );
};

export default StudioAuditBoard;
